function parseHhMm(time) {
    const parts = time.split(':');
    const hh = Number(parts[0]);
    const mm = Number(parts[1]);
    if (!Number.isInteger(hh) || !Number.isInteger(mm))
        return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59)
        return null;
    return { hh, mm };
}
/**
 * Every round the schedule announces in a day, earliest first.
 *
 * The single place that reads the legacy `drawTime`: a lottery configured before
 * multi-round support reads back as exactly one round, so nothing about it changes.
 * Rounds with an unparseable time are dropped rather than throwing — one bad entry
 * must not stop the other rounds of that lottery from opening.
 *
 * The array position becomes the draw's round_index, so the sort matters: 'HH:MM' is
 * zero-padded, which makes lexical order chronological.
 */
export function normalizeRounds(cfg) {
    if (cfg.type === 'manual')
        return [];
    const raw = cfg.rounds && cfg.rounds.length > 0
        ? cfg.rounds
        : cfg.drawTime
            ? [{ time: cfg.drawTime }]
            : [];
    return raw
        .filter((r) => parseHhMm(r.time) !== null)
        .slice()
        .sort((a, b) => a.time.localeCompare(b.time));
}
/** Does this local calendar day announce at all? (weekday / month-date filters) */
function dayAnnounces(cfg, dayLocal) {
    if (cfg.type === 'daily')
        return true;
    if (cfg.type === 'weekly')
        return cfg.days?.includes(dayLocal.getUTCDay()) ?? false;
    if (cfg.type === 'monthly')
        return cfg.dates?.includes(dayLocal.getUTCDate()) ?? false;
    return false;
}
/**
 * The next slot strictly after `after`, or null for a 'manual' schedule (admin creates
 * draws by hand) or malformed config. `after` and the result are UTC.
 */
export function nextDrawSlot(cfg, after) {
    const rounds = normalizeRounds(cfg);
    if (rounds.length === 0)
        return null;
    const offMs = (cfg.tzOffsetHours ?? 7) * 3_600_000;
    // Shift `after` into local wall-clock, read as UTC fields.
    const afterLocal = new Date(after.getTime() + offMs);
    for (let d = 0; d < 366; d++) {
        const dayLocal = new Date(Date.UTC(afterLocal.getUTCFullYear(), afterLocal.getUTCMonth(), afterLocal.getUTCDate() + d));
        if (!dayAnnounces(cfg, dayLocal))
            continue;
        for (let i = 0; i < rounds.length; i++) {
            const round = rounds[i];
            const { hh, mm } = parseHhMm(round.time);
            // Candidate local wall time (as UTC fields), then convert back to real UTC.
            const candLocal = Date.UTC(dayLocal.getUTCFullYear(), dayLocal.getUTCMonth(), dayLocal.getUTCDate(), hh, mm, 0);
            const realUtc = new Date(candLocal - offMs);
            if (realUtc.getTime() <= after.getTime())
                continue;
            return {
                at: realUtc,
                roundIndex: i,
                ...(round.labelTh !== undefined ? { labelTh: round.labelTh } : {}),
                ...(round.closeBeforeSeconds !== undefined
                    ? { closeBeforeSeconds: round.closeBeforeSeconds }
                    : {}),
                ...(round.openBeforeSeconds !== undefined
                    ? { openBeforeSeconds: round.openBeforeSeconds }
                    : {}),
                ...(round.openAfterPreviousSeconds !== undefined
                    ? { openAfterPreviousSeconds: round.openAfterPreviousSeconds }
                    : {}),
            };
        }
    }
    return null;
}
/**
 * The next `count` slots — for the lobby's "งวดถัดไป" list and the admin's schedule
 * preview. Both consoles derive upcoming slots from the same maths the server opens
 * งวด with, so what a member is promised is what actually gets created.
 */
export function nextDrawSlots(cfg, count, after) {
    const out = [];
    let cursor = after;
    for (let i = 0; i < count; i++) {
        const slot = nextDrawSlot(cfg, cursor);
        if (!slot)
            break;
        out.push(slot);
        cursor = slot.at;
    }
    return out;
}
/**
 * Next draw time strictly after `after`, or null for a 'manual' schedule or malformed
 * config. Kept for callers that only need the instant; anything that has to know which
 * round of the day it landed on wants {@link nextDrawSlot}.
 */
export function computeNextDrawAt(cfg, after) {
    return nextDrawSlot(cfg, after)?.at ?? null;
}
/**
 * Beyond this gap to the previous round, betOpenAfterPreviousSeconds no longer applies —
 * the gap reads as a scheduling break (a weekend), not the next round in the same run.
 * หวยหุ้นดาวโจนส์'s own weekday gap is 24h (Tue 03:20 → Wed 03:20); its weekend gap is
 * 72h (Sat → Tue). 36h sits cleanly between the two without depending on either exactly.
 */
const OPEN_AFTER_PREVIOUS_MAX_GAP_MS = 36 * 3_600_000;
/**
 * The betting window for a slot, given how far ahead of the announcement betting
 * closes. `from` is when betting would start (usually now).
 *
 * Returns null when the window has already shut — a slot whose close time has passed
 * cannot be opened: it would either run with close_at in the past, or (if clamped to
 * the announcement) leave betting open until the result is readable.
 */
export function drawWindow(drawAt, betCloseBeforeSeconds, from, betOpenBeforeSeconds, 
/**
 * The previous round's own announcement, and how long after it this round opens —
 * see `betOpenAfterPreviousSeconds` on `DrawLottery`. Takes over openAt entirely when
 * both are given and the gap is short enough; otherwise openAt falls back to
 * `betOpenBeforeSeconds` exactly as before these two parameters existed.
 */
previousDrawAt, betOpenAfterPreviousSeconds) {
    const closeAt = new Date(drawAt.getTime() - betCloseBeforeSeconds * 1000);
    if (closeAt <= from)
        return null;
    // The gap must be a real one, forwards: `previousDrawAt` is the lottery's most recent
    // งวด, which on a lottery that opens งวด ahead of time is an announcement still in the
    // future. A slot that falls BEFORE it — an admin adding an earlier round mid-day — has
    // no previous round to follow, and the unsigned comparison would have accepted the
    // negative gap and anchored the opening to an announcement that comes after the
    // result, producing a งวด that can never take a bet.
    const gapToPrevious = previousDrawAt == null
        ? null
        : drawAt.getTime() - previousDrawAt.getTime();
    const afterPrevious = betOpenAfterPreviousSeconds != null && previousDrawAt != null && gapToPrevious != null
        && gapToPrevious > 0 && gapToPrevious <= OPEN_AFTER_PREVIOUS_MAX_GAP_MS
        ? new Date(previousDrawAt.getTime() + betOpenAfterPreviousSeconds * 1000)
        : null;
    // Default (both null): betting starts the moment the งวด is created, which is what
    // every lottery did before this was configurable. With betOpenBeforeSeconds set, it
    // starts a fixed distance ahead of the announcement instead — หวยไทย opens ten days
    // out. That instant may already be past, in which case the งวด simply opens now; the
    // caller records it as openAt either way and the status follows from openAt <= now.
    const openAt = afterPrevious ?? (betOpenBeforeSeconds == null
        ? from
        : new Date(drawAt.getTime() - betOpenBeforeSeconds * 1000));
    const openAtFromSchedule = afterPrevious != null || betOpenBeforeSeconds != null;
    return { openAt, closeAt, drawAt, openAtFromSchedule };
}
/**
 * How long before the announcement betting shuts for a given slot. A round may set its
 * own cutoff — two rounds hours apart cannot share one — and falls back to the
 * lottery's when it does not.
 */
export function slotCloseBeforeSeconds(slot, lotteryDefault) {
    return slot.closeBeforeSeconds ?? lotteryDefault;
}
/**
 * How long before the announcement betting opens for a given slot, or null for "when the
 * งวด is created". The lottery's value is an offset from each round's own announcement,
 * so on a multi-round lottery it lands on a different wall-clock time for every round —
 * see openBeforeSeconds on DrawRoundConfig for why that is rarely what was meant.
 */
export function slotOpenBeforeSeconds(slot, lotteryDefault) {
    return slot.openBeforeSeconds ?? lotteryDefault ?? null;
}
/**
 * How long after the previous round's announcement this slot starts taking bets, or null
 * for "that rule does not apply here". The lottery-wide value applies to every round
 * alike, which on a multi-round lottery chains the first round of a day to the last round
 * of the day before; a round sets its own to opt in on its own.
 */
export function slotOpenAfterPreviousSeconds(slot, lotteryDefault) {
    return slot.openAfterPreviousSeconds ?? lotteryDefault ?? null;
}
/** The announcement day (YYYY-MM-DD) a draw belongs to, in the schedule's timezone. */
export function drawDateOf(drawAt, tzOffsetHours) {
    return new Date(drawAt.getTime() + tzOffsetHours * 3_600_000).toISOString().slice(0, 10);
}
//# sourceMappingURL=draw-schedule.js.map